import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';

const STATUS_STYLES = {
  queued: 'bg-yellow-100 text-yellow-800',
  running: 'bg-blue-100 text-blue-800',
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

const REFRESH_MS = 3000;

const FilingQueue = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [logText, setLogText] = useState('');
  const prevStatuses = useRef({});
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const loadQueue = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/filing-queue${params}`);
      const next = res.data.data || [];
      setItems(next);

      next.forEach(item => {
        const prev = prevStatuses.current[item._id];
        if (prev === 'running' && item.status === 'success') {
          toast.success(`Filed: ${item.ipindiaReference}`);
        } else if (prev === 'running' && item.status === 'failed') {
          toast.error(item.lastError || 'Filing failed');
        }
        prevStatuses.current[item._id] = item.status;
      });

      const selected = next.find(i => i._id === selectedIdRef.current);
      if (selected?.log) setLogText(selected.log);
    } catch (e) {
      console.error(e);
      if (!silent) toast.error('Failed to load filing queue');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (!isAdmin) return;
    loadQueue();
    const id = setInterval(() => loadQueue(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [loadQueue, isAdmin]);

  const handleRetry = async (item) => {
    try {
      setSelectedId(item._id);
      setLogText('Queued for retry — worker will start shortly...\n');
      await api.post(`/filing-queue/${item._id}/retry`);
      toast.success('Retry queued');
      loadQueue(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Retry failed');
    }
  };

  const handleCancel = async (item) => {
    if (!window.confirm('Cancel this queued filing?')) return;
    try {
      await api.post(`/filing-queue/${item._id}/cancel`);
      toast.success('Cancelled');
      loadQueue();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Cancel failed');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 text-center text-gray-500">Admin access required.</div>
    );
  }

  const hasActive = items.some(i => ['queued', 'running', 'failed'].includes(i.status));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">TM-A Filing Scheduler</h1>
          <p className="text-sm text-gray-500 mt-1">
            Filings start automatically when added to the queue. Failed filings retry until they succeed (unless cancelled).
          </p>
          {hasActive && (
            <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
              <ClockIcon className="h-4 w-4 animate-pulse" />
              Scheduler active — processing queue…
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="running">Running</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            onClick={() => loadQueue()}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
          >
            <ArrowPathIcon className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white shadow rounded-lg overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No items in queue.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client / App</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trademark / Class</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">IPINDIA / Temp</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => (
                  <tr
                    key={item._id}
                    className={selectedId === item._id ? 'bg-indigo-50' : 'hover:bg-gray-50'}
                    onClick={() => {
                      setSelectedId(item._id);
                      setLogText(item.log || '');
                    }}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{item.clientName || '—'}</div>
                      <Link
                        to={`/trademark-applications/${item.applicationId}`}
                        className="text-xs text-indigo-600 hover:underline"
                        onClick={e => e.stopPropagation()}
                      >
                        {item.applicationNumber}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <div>{item.trademarkName || '—'}</div>
                      <div className="text-xs text-gray-500">Class {item.classNumber}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[item.status] || ''}`}>
                        {item.status}
                        {item.retryCount > 0 && (
                          <span className="ml-1">· attempt {item.retryCount}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {item.ipindiaReference ? (
                        <span className="text-green-700 font-medium">{item.ipindiaReference}</span>
                      ) : item.tempApplicationNo ? (
                        <span className="text-amber-700" title="Temp application no. from partial filing">
                          Temp: {item.tempApplicationNo}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                      {item.lastError && item.status === 'failed' && (
                        <div className="text-red-600 mt-0.5 truncate max-w-[140px]" title={item.lastError}>
                          {item.lastError}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {item.status === 'queued' && (
                        <span className="text-xs text-gray-500 mr-2">Starting…</span>
                      )}
                      {item.status === 'failed' && (
                        <button
                          onClick={() => handleRetry(item)}
                          className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 mr-1"
                          title="Queue immediate retry (otherwise auto-retries every few seconds)"
                        >
                          Retry now
                        </button>
                      )}
                      {['queued', 'failed'].includes(item.status) && (
                        <button
                          onClick={() => handleCancel(item)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      )}
                      {item.status === 'running' && (
                        <ClockIcon className="h-5 w-5 text-blue-500 inline animate-pulse" />
                      )}
                      {item.status === 'success' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 inline" />
                      )}
                      {item.status === 'failed' && (
                        <span className="text-xs text-amber-600 ml-1">Will retry</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Live Log</h2>
          {selectedId ? (
            <div className="bg-gray-900 text-green-400 text-xs font-mono p-3 rounded-md h-96 overflow-y-auto whitespace-pre-wrap">
              {logText || 'Waiting for automation output…'}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Click a row to view its log.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilingQueue;
